/**
 * Exploration commands (plan R18, §2.9 flow 2 tabs Charts and Scanner).
 * - `sellCharts(stationId)`: a `chart-buyer` station of the current system within hail range pays every unsold chart
 *   (`career.earn(sellAllCharts)`), `REV.chartsSold`.
 * - `buyScanner(stationId, mk)`: an `outfitting` station within hail range sells scanner Mk II or III at the stand-in
 *   price; a downgrade or the same Mk is refused.
 * - `buySurvey(stationId)` (M8 MC-a10, D-360 RD7(d)): a station of a survey faction (the Holdfast) within hail range sells
 *   its survey for `survey.priceByRegion[region]` (`career.spend`, a sink), then charts every rumoured or unknown system
 *   within `survey.jumps` relay hops and lists it in `charts.surveyed`, so its first visit pays only POI richness
 *   (`charting.ts systemValued`). No `REV.systemCharted` is sent: that event readies chart contracts, which still need
 *   the visit. Refused with nothing left to survey.
 *
 * Refusals never change anything. Rare (UI): allocates freely.
 */
import type { StationService, StationSite } from '../galaxy/layout/types'
import { emitReach, REV } from '../reach/events'
import type { ReachHeadlessCore } from '../reach/runtime/core'
import type { ReachCore } from '../reach/runtime/types'
import { loadSessionConfig } from '../reach/session/config'
import { inHailRange } from '../reach/session/hailRange'
import { loadStandInConfig, standInPrices, type StandInConfig } from '../reach/standin'
import { markCharted, sellAllCharts, unsoldTotal } from '../reach/state'
import type { ReachCommand, ReachCommandResult, ReachState } from '../reach/types'
import type { Galaxy } from '../galaxy/types'
import type { Sim } from '../sim'
import { Life, Pos64 } from '../world/components'
import { emitRumoured } from './charting'
import { sellsSurvey, type ExploreConfig } from './config'
import { systemsWithinHops } from './rewards'

let defaultHailRangeM: number | null = null
let defaultStandIn: StandInConfig | null = null

/** The live hail range of the full core (the dev panel tunes it), else the bundled value. */
export function hailRangeOf(core: ReachCore): number {
  const configs = (core as Partial<Pick<ReachHeadlessCore, 'configs'>>).configs
  if (configs !== undefined) return configs.session.hail.rangeM
  defaultHailRangeM ??= loadSessionConfig().hail.rangeM
  return defaultHailRangeM
}

/** The live stand-in prices config of the full core, else the bundled one. */
export function standInConfigOf(core: ReachCore): StandInConfig {
  const configs = (core as Partial<Pick<ReachHeadlessCore, 'configs'>>).configs
  if (configs !== undefined) return configs.standIn
  defaultStandIn ??= loadStandInConfig()
  return defaultStandIn
}

type StationCheck = { ok: true; station: StationSite } | { ok: false; result: ReachCommandResult }

/**
 * The station if it is in this system, offers `service` (null: any station), and the living player is within hail range
 * of its hull (`inHailRange`, SC-5a2b).
 */
export function stationFor(core: ReachCore, sim: Sim, stationId: string, service: StationService | null, label: string): StationCheck {
  const station = core.session.layout?.stations.find((s) => s.id === stationId)
  if (station === undefined) return { ok: false, result: { ok: false, error: 'invalid', message: 'That station is not in this system' } }
  if (service !== null && !station.services.includes(service)) return { ok: false, result: { ok: false, error: 'invalid', message: `${station.name} does not ${label}` } }
  const p = sim.playerEid
  const range = hailRangeOf(core)
  if (p < 0 || Life.alive[p] !== 1) return { ok: false, result: { ok: false, error: 'outOfRange', message: `Hail ${station.name} first` } }
  if (!inHailRange(station, Pos64.x[p], Pos64.y[p], Pos64.z[p], range)) {
    return { ok: false, result: { ok: false, error: 'outOfRange', message: `Within ${Math.round(range / 1000)} km of the hull of ${station.name}` } }
  }
  return { ok: true, station }
}

export function sellCharts(core: ReachCore, sim: Sim, cmd: Extract<ReachCommand, { kind: 'sellCharts' }>): ReachCommandResult {
  const check = stationFor(core, sim, cmd.stationId, 'chart-buyer', 'buy chart data')
  if (!check.ok) return check.result
  if (!(unsoldTotal(core.state) > 0)) return { ok: false, error: 'invalid', message: 'No chart data to sell' }
  const credits = sellAllCharts(core.state)
  core.career.earn(credits, 'charts')
  const at = check.station.position
  emitReach(core.events, REV.chartsSold, 0, 0, at[0], at[1], at[2], credits)
  return { ok: true, message: `Chart data sold: ${credits} credits` }
}

export function buyScanner(core: ReachCore, sim: Sim, cmd: Extract<ReachCommand, { kind: 'buyScanner' }>): ReachCommandResult {
  const mk = cmd.mk
  if (mk !== 2 && mk !== 3) return { ok: false, error: 'invalid', message: 'No such scanner' }
  const check = stationFor(core, sim, cmd.stationId, 'outfitting', 'fit scanners')
  if (!check.ok) return check.result
  const current = core.career.scannerMk()
  if (mk <= current) {
    return { ok: false, error: 'invalid', message: mk === current ? `Scanner Mk ${roman(mk)} is already fitted` : `Scanner Mk ${roman(current)} is better` }
  }
  const price = standInPrices(standInConfigOf(core)).scanners[mk]
  if (!core.career.spend(price, `scanner Mk ${mk}`)) return { ok: false, error: 'funds', message: `Scanner Mk ${roman(mk)} costs ${price} credits` }
  core.career.setScannerMk(mk)
  return { ok: true, message: `Scanner Mk ${roman(mk)} fitted` }
}

/** The rumoured or unknown systems a survey bought in `systemId` charts: within `jumps` relay hops, nearest first. */
export function surveyTargets(state: ReachState, galaxy: Galaxy, systemId: string, jumps: number): string[] {
  return systemsWithinHops(galaxy, systemId, jumps).filter((id) => state.knowledge[id] !== 'charted')
}

export function buySurvey(core: ReachCore, sim: Sim, cmd: Extract<ReachCommand, { kind: 'buySurvey' }>, cfg: ExploreConfig): ReachCommandResult {
  const check = stationFor(core, sim, cmd.stationId, null, 'sell surveys')
  if (!check.ok) return check.result
  const station = check.station
  const layout = core.session.layout
  if (layout === null || !sellsSurvey(cfg, station.faction)) return { ok: false, error: 'invalid', message: `${station.name} does not sell survey data` }
  const state = core.state
  const targets = surveyTargets(state, core.galaxy, layout.systemId, cfg.survey.jumps)
  if (targets.length === 0) return { ok: false, error: 'invalid', message: `Every system within ${cfg.survey.jumps} jumps is charted` }
  const price = cfg.survey.priceByRegion[layout.region]
  if (!core.career.spend(price, 'survey')) return { ok: false, error: 'funds', message: `The survey costs ${price} credits` }
  const surveyed = (state.charts.surveyed ??= [])
  for (const id of targets) {
    emitRumoured(core, markCharted(state, core.galaxy, id).rumoured)
    if (!surveyed.includes(id)) surveyed.push(id)
  }
  return { ok: true, message: `Survey bought: ${targets.length} ${targets.length === 1 ? 'system' : 'systems'} charted` }
}

function roman(mk: number): string {
  return mk === 3 ? 'III' : mk === 2 ? 'II' : 'I'
}
